import React, { useMemo, useCallback } from "react";
import {
  Button as ButtonBase,
  Container,
  FormLabel,
  Grid as GridBase,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import RpDatePickerBase from "@/components/muiCustom/date/RpDatePicker";
import RpCurrencyFieldBase from "@/components/muiCustom/textField/RpCurrencyField";
import CreativesBase from "../creatives/Creatives";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { getDigitalCampaignLocationDisplayName } from "@/features/campaigns/digital/launch-digital-campaign/utils/locationDisplayName";
import {
  getPackageChannelOptions,
  isParticipantChannelSelectionEnabled,
  pruneSelectedChannelsForPackages,
  shouldPruneChannelsForPackageChange,
} from "@/features/campaigns/shared/helpers/channelSelectionHelper";
import {
  canShowImpressionEstimation,
  getImpressionEstimation,
  getEstimatedImpressions,
} from "@/features/campaigns/shared/helpers/estimatedImpressionsHelper";
import {
  EstimatedImpressionsDisclosure,
  EstimatedImpressionsVisibilityProvider,
  EstimatedImpressionsText,
} from "@/features/campaigns/shared/components/EstimatedImpressions";

type LocationId = string | number;

type EditableLocation = {
  locationId?: LocationId;
  creatives?: LocationId[];
  channels?: LocationId[];
  enabledChannels?: LocationId[];
  start_date?: unknown;
  end_date?: unknown;
  budget?: unknown;
  [key: string]: unknown;
};

type CampaignLocation = {
  id?: LocationId;
  location_name?: string;
  creatives?: Array<Record<string, unknown>>;
  [key: string]: unknown;
};

type CampaignData = {
  can_participant_select_specific_channels?: unknown;
  creative_select_flag?: boolean;
  creative_select_max_value?: number;
  campaign_type?: string;
  evergreen_participant_date_flag?: boolean;
  locations: {
    subscribed: CampaignLocation[];
  };
  [key: string]: unknown;
};

type JoinCampaignLocationProps = {
  loc: EditableLocation;
  campaign: CampaignData;
  onClose?: () => void;
  onSave?: (_location: EditableLocation & { amount: unknown }) => void;
};

const RpDatePicker = RpDatePickerBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const RpCurrencyField = RpCurrencyFieldBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const Creatives = CreativesBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const Grid = GridBase as unknown as React.ComponentType<
  React.ComponentProps<typeof GridBase> & {
    item?: boolean;
  }
>;
const Button = ButtonBase as unknown as React.ComponentType<
  React.ComponentProps<typeof ButtonBase> & {
    onClick?: unknown;
  }
>;

function JoinCampaignLocation({
  loc,
  campaign,
  onClose,
  onSave,
}: JoinCampaignLocationProps) {
  const hideField = useMemo(() => {
    return joinCampaignHelper.hideField(campaign);
  }, [campaign]);

  const locationDetails = useMemo(() => {
    return campaign.locations.subscribed.find(
      (l) => String(l.id) === String(loc.locationId)
    );
  }, [loc, campaign]);

  const locName = useMemo(
    () => getDigitalCampaignLocationDisplayName(locationDetails),
    [locationDetails]
  );

  const validationSchema = useMemo(() => {
    return Yup.object({
      creatives: Yup.array()
        .min(1, "At least one creative is required")
        .max(
          campaign?.creative_select_flag
            ? campaign?.creative_select_max_value
            : 1,
          `Up to ${
            campaign?.creative_select_flag
              ? campaign?.creative_select_max_value
              : 1
          } creatives are allowed`
        ),
      start_date: Yup.date().required("Start date is required"),
      end_date: Yup.date()
        .min(Yup.ref("start_date"), "End date must be after start date")
        .required("End date is required"),
      budget: Yup.number()
        .required("Budget is required")
        .test(
          "budget-range",
          "Budget must be between minimum and maximum budget",
          (value, ctx) => {
            const minMaxRequired = joinCampaignHelper.getRequiredMinMax(
              ctx.parent,
              campaign
            );

            if (
              value < minMaxRequired?.minRequiredBudget ||
              value > minMaxRequired?.maxRequiredBudget
            ) {
              return ctx.createError({
                message: `We recommend a minimum budget between $${minMaxRequired?.minRequiredBudget} and $${minMaxRequired?.maxRequiredBudget}`,
              });
            }

            return true;
          }
        ),
    });
  }, [campaign]);

  const handleOnSubmit = useCallback(
    (values: EditableLocation & { budget: unknown }) => {
      onSave?.({ ...loc, ...values, amount: values.budget });
    },
    [loc, onSave]
  );

  const formik = useFormik({
    initialValues: {
      creatives: loc?.creatives || [],
      channels: loc?.channels || loc?.enabledChannels || [],
      start_date: loc?.start_date,
      end_date: loc?.end_date,
      budget: loc?.budget,
    },
    validationSchema,
    onSubmit: handleOnSubmit,
    enableReinitialize: true,
  });

  const dateConstraints = useMemo(() => {
    const {
      campaign_type,
      evergreen_participant_date_flag,
      // evergreen_participant_min_value,
    } = campaign;

    let minStartDate;
    let maxStartDate;
    let minDate;
    let maxDate;
    let disabled = true;

    if (campaign_type === "Evergreen" && evergreen_participant_date_flag) {
      minStartDate = joinCampaignHelper.getCampaignMinMaxStartDate(
        locationDetails,
        campaign
      )?.minStartDate;
      maxStartDate = joinCampaignHelper.getCampaignMinMaxStartDate(
        locationDetails,
        campaign
      )?.maxStartDate;

      minDate = joinCampaignHelper.getCampaignMinMaxEndDate(
        formik.values.start_date || minStartDate,
        locationDetails,
        campaign
      ).minEndDate;

      maxDate = joinCampaignHelper.getCampaignMinMaxEndDate(
        formik.values.start_date || minStartDate,
        locationDetails,
        campaign
      ).maxEndDate;

      // if (moment(loc?.subscription?.subscription_end_date).date() === 30) {
      //   maxStartDate = moment(loc?.subscription?.subscription_end_date)
      //     .subtract(evergreen_participant_min_value, "days")
      //     .add(2, "days");
      // } else {
      //   maxStartDate = moment(loc?.subscription?.subscription_end_date)
      //     .subtract(evergreen_participant_min_value, "days")
      //     .add(1, "days");
      // }
      disabled = false;
    }

    return {
      minStartDate,
      maxStartDate,
      minDate,
      maxDate,
      disabled,
    };
  }, [campaign, formik.values.start_date, locationDetails]);

  const selectedPackages = useMemo(() => {
    const selectedCreativeIds = new Set(
      (formik.values.creatives || []).map((creativeId) => String(creativeId))
    );
    return (locationDetails?.creatives || []).filter((creative) =>
      selectedCreativeIds.has(String(creative.creative_id))
    );
  }, [locationDetails?.creatives, formik.values.creatives]);

  const estimatedImpressions = useMemo(() => {
    const packageChannels = getPackageChannelOptions(selectedPackages);
    const channels = getImpressionEstimation(campaign)
      ?.canParticipantSelectSpecificChannels
      ? packageChannels.filter((channel) =>
          (formik.values.channels || []).some(
            (channelId) => String(channelId) === String(channel.id)
          )
        )
      : packageChannels;

    return getEstimatedImpressions({
      budget: formik.values.budget as string | number | null,
      brandContribution: loc?.brand_contribution_amount as
        | string
        | number
        | null,
      impressionEstimation: getImpressionEstimation(campaign),
      channels,
      restrictToChannels: true,
    });
  }, [
    campaign,
    formik.values.budget,
    formik.values.channels,
    loc?.brand_contribution_amount,
    selectedPackages,
  ]);
  const isBudgetOutsideRecommendedRange = Boolean(
    joinCampaignHelper.getRequiredMinMax(formik.values, campaign)
  );
  const canShowEstimatedImpressions =
    canShowImpressionEstimation(campaign) &&
    selectedPackages.length > 0 &&
    campaign?.budget_type === "Location Specific" &&
    Boolean(formik.values.budget) &&
    !isBudgetOutsideRecommendedRange;

  const handleOnSelectCreative = (selectedCreatives: LocationId[]) => {
    formik.setFieldValue("creatives", selectedCreatives);
    if (
      isParticipantChannelSelectionEnabled(campaign) &&
      shouldPruneChannelsForPackageChange({
        previousPackageIds: formik.values.creatives,
        nextPackageIds: selectedCreatives,
      })
    ) {
      formik.setFieldValue(
        "channels",
        pruneSelectedChannelsForPackages({
          creatives: locationDetails?.creatives,
          selectedPackageIds: selectedCreatives,
          selectedChannelIds: formik.values.channels,
        })
      );
    }
    formik.validateField("creatives");
  };

  const handleOnSelectChannels = (selectedChannels: LocationId[]) => {
    formik.setFieldValue("channels", selectedChannels);
  };

  return (
    <EstimatedImpressionsVisibilityProvider>
      <div>
        <div className="text-center mb-3">
          <Typography variant="h6">Edit this location</Typography>
          <Typography variant="body2">{locName}</Typography>
        </div>

        <Container>
          <div className="row mb-3">
            <div className="col-lg-12">
              <Creatives
                creatives={locationDetails?.creatives}
                onSelectCreative={handleOnSelectCreative}
                initiallySelected={formik.values.creatives}
                locationName={locationDetails?.location_name}
                loc={locationDetails}
                campaign={campaign}
                helperText={formik.errors.creatives}
                selectedChannelIds={formik.values.channels}
                onSelectChannels={handleOnSelectChannels}
                //   productList={productList}
                //   getAdvanceSettingData={getAdvanceSettingData}
                //   onFillSettingsData={handleOnFillSettingData}
                //   initialAnswers={locFormData.advance_setting}
                //   initialDisabled={isUnsubscribed}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <FormLabel className="fw-sb text-dark mb-3">
                Campaign Dates
              </FormLabel>
              <div className="row">
                <div className="col-md-6 mb-2 mb-md-0">
                  <RpDatePicker
                    name="start_date"
                    value={formik.values.start_date}
                    onChange={(value: any) => {
                      formik.setFieldValue(
                        "start_date",
                        value.format("YYYY-MM-DD")
                      );
                      formik.setFieldValue("end_date", "");
                    }}
                    onBlur={() => formik.setFieldTouched("start_date", true)}
                    error={
                      formik.touched.start_date && formik.errors.start_date
                    }
                    helperText={
                      formik.touched.start_date && formik.errors.start_date
                    }
                    disabled={hideField.startDate || dateConstraints.disabled}
                    minDate={dateConstraints.minStartDate}
                    maxDate={dateConstraints.maxStartDate}
                    placeholder="Start Date"
                  />
                </div>
                <div className="col-md-6 mb-2 mb-md-0">
                  <RpDatePicker
                    name="end_date"
                    value={formik.values.end_date}
                    onChange={(value: any) =>
                      formik.setFieldValue(
                        "end_date",
                        value.format("YYYY-MM-DD")
                      )
                    }
                    onBlur={() => formik.setFieldTouched("end_date", true)}
                    error={formik.touched.end_date && formik.errors.end_date}
                    helperText={
                      formik.touched.end_date && formik.errors.end_date
                    }
                    disabled={hideField.endDate || dateConstraints.disabled}
                    minDate={dateConstraints.minDate}
                    maxDate={dateConstraints.maxDate}
                    placeholder="End Date"
                  />
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <FormLabel className="fw-sb text-dark mb-3">
                Location Budget
              </FormLabel>
              <RpCurrencyField
                name="budget"
                value={formik.values.budget}
                onChange={(e: any) =>
                  formik.setFieldValue("budget", e.target.value)
                }
                onBlur={() => formik.setFieldTouched("budget", true)}
                error={formik.touched.budget && formik.errors.budget}
                helperText={formik.touched.budget && formik.errors.budget}
                disabled={hideField.locationBudget}
              />
              <EstimatedImpressionsText
                enabled={canShowEstimatedImpressions}
                estimate={
                  canShowEstimatedImpressions ? estimatedImpressions : null
                }
              />
              {canShowEstimatedImpressions ? (
                <EstimatedImpressionsDisclosure enabled />
              ) : null}
            </div>
          </div>

          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
            className="mt-3 justify-content-center justify-content-sm-end"
          >
            <Grid item className="text-center text-sm-left">
              <Button
                variant="outlined"
                disableElevation
                className="px-5 mr-sm-2 mb-2"
                onClick={onClose}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item className="text-center text-sm-right">
              <Button
                disabled={!formik.isValid}
                variant="contained"
                color="primary"
                disableElevation
                className="px-4 mb-2"
                onClick={formik.handleSubmit as any}
              >
                Save and Confirm
              </Button>
            </Grid>
          </Grid>
        </Container>
      </div>
    </EstimatedImpressionsVisibilityProvider>
  );
}

export default JoinCampaignLocation;
